import { useEffect, useMemo, useRef, useState } from 'react'
import { db, type CacheRow } from '../net/localDb.js'
import { subscribe, type SyncStatus } from '../net/syncEngine.js'
import { search, type Searchable, type SearchOptions } from '../net/search.js'

/**
 * Liest eine Cache-Tabelle und aktualisiert sich, wenn der Abgleich etwas
 * geändert hat. Kein Netzzugriff – die Bildschirme lesen ausschliesslich
 * lokal, und genau deshalb reagieren sie sofort.
 */
export function useCacheTable<T extends CacheRow>(
  table: string
): {
  rows: T[]
  loading: boolean
  reload: () => void
} {
  const [rows, setRows] = useState<T[]>([])
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const [status, setStatus] = useState<SyncStatus | null>(null)

  useEffect(() => subscribe(setStatus), [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const all = (await db.table(table).toArray()) as T[]
      if (!cancelled) {
        setRows(all)
        setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [table, tick, status?.changeSeq, status?.lastSyncAt])

  return { rows, loading, reload: () => setTick((t) => t + 1) }
}

/**
 * Die Einstellungen des Mandanten aus dem lokalen Zwischenspeicher.
 *
 * Wie useCacheTable und aus demselben Grund: Der Abgleich kann noch laufen,
 * wenn ein Bildschirm erscheint – direkt nach dem Anmelden ist das der
 * Normalfall. Ein einmaliges Lesen liefert dann nichts und erfährt nie, dass
 * die Daten inzwischen da sind.
 *
 * Das war lange harmlos, weil der Firmenname nur als Bildbeschreibung des
 * Logos diente. Seit er im Kopf der Seitenleiste steht, wenn kein Logo
 * hochgeladen ist, ist er das Einzige, was dort steht – und eine leere
 * Kopfzeile fällt sofort auf.
 */
export function useEinstellungen(): { firma: string; logoKey: string | null } {
  const [werte, setWerte] = useState<{ firma: string; logoKey: string | null }>({
    firma: '',
    logoKey: null
  })
  const [status, setStatus] = useState<SyncStatus | null>(null)

  useEffect(() => subscribe(setStatus), [])

  useEffect(() => {
    let weg = false
    void db.settings.get('tenant').then((row) => {
      if (weg || !row) return
      const r = row as Record<string, unknown>
      setWerte({
        firma: String(r.companyName ?? ''),
        logoKey: r.logoKey ? String(r.logoKey) : null
      })
    })
    return () => {
      weg = true
    }
  }, [status?.changeSeq, status?.lastSyncAt])

  return werte
}

/**
 * Suche über den lokalen Bestand, entprellt.
 *
 * Die Entprellung ist mit 60 ms bewusst kurz: sie soll nur Tastenanschläge
 * bündeln, nicht auf den Benutzer warten. Die Suche selbst dauert bei den
 * Beständen eines kleinen Betriebs wenige Millisekunden.
 */
export function useSearch<T extends Searchable>(
  rows: readonly T[],
  options: SearchOptions<T>
): {
  query: string
  setQuery: (q: string) => void
  results: T[]
  tookMs: number
} {
  const [query, setQuery] = useState('')
  const [applied, setApplied] = useState('')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [tookMs, setTookMs] = useState(0)

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => setApplied(query), 60)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [query])

  const results = useMemo(() => {
    const t0 = performance.now()
    const out = search(rows, applied, options)
    setTookMs(Math.round((performance.now() - t0) * 100) / 100)
    return out
    // options ist eine Konstante je Bildschirm – absichtlich nicht in den Abhängigkeiten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, applied])

  return { query, setQuery, results, tookMs }
}

/** Kleine Hilfe für Formulare, ohne Formularbibliothek. */
export function useForm<T extends object>(
  initial: T
): {
  values: T
  set: <K extends keyof T>(key: K, value: T[K]) => void
  reset: (next?: T) => void
} {
  const [values, setValues] = useState<T>(initial)
  return {
    values,
    set: (key, value) => setValues((v) => ({ ...v, [key]: value })),
    reset: (next) => setValues(next ?? initial)
  }
}
